// ./src/hooks/useEventSetupCatalog.ts
//
// The event setup catalog (ROADMAP A2b #62) — every event type with its synonyms and offers, and the setup rules —
// read ONCE and shared app-wide (EventSetupCatalogProvider). Every lookup the app makes about an event type goes
// through the helpers here, which is what retired the hand-kept FE mirror (`eventTypeCatalog.ts`'s 33-type list,
// which could not show the 14 sub-types and had drifted from the seed).
//
// ⚠ NOT A COLLECTION HOOK: one read, no paging, no search round-trips — the catalog is a few hundred rows and the
// type search must answer on every keystroke, so it is searched locally (`utils/eventTypeSearch`).
// ⓘ Not account-specific (the server serves it publicly), so there is nothing to reset on sign-out.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OpResult, OP_RESULT_CODES } from '@sdflc/api-helpers';

import { EventSetupCatalog, EventSetupRuleRow, EventSetupService, EventSetupType } from '@/utils/api';
import { EventTypeMatch, searchEventTypes } from '@/utils/eventTypeSearch';

export interface UseEventSetupCatalogOptions {
  autoLoad?: boolean;
}

export interface UseEventSetupCatalogReturn {
  types: EventSetupType[];
  rules: EventSetupRuleRow[];
  loading: boolean;
  isLoaded: boolean;
  lastError: OpResult | null;
  reload: () => Promise<void>;
  /** A type by id OR code (the wizard's `?category=` deep link accepts either). Null while loading / unknown. */
  findType: (idOrCode: string | null | undefined) => EventSetupType | null;
  /** The parent of a sub-type (Holiday Party for Christmas), else null. */
  parentOf: (type: EventSetupType | null | undefined) => EventSetupType | null;
  /** Top-level types of one group, in catalog order. */
  topLevelOfGroup: (group: string) => EventSetupType[];
  /** A type's sub-types, in catalog order. */
  childrenOf: (typeId: string | null | undefined) => EventSetupType[];
  /** May an event of this type repeat? ⚠ TRUE when unknown — never hide "Repeats" because the catalog is loading. */
  allowsRecurring: (idOrCode: string | null | undefined) => boolean;
  search: (query: string) => EventTypeMatch<EventSetupType>[];
}

export const useEventSetupCatalog = (options: UseEventSetupCatalogOptions = {}): UseEventSetupCatalogReturn => {
  const { autoLoad = true } = options;

  const [catalog, setCatalog] = useState<EventSetupCatalog | null>(null);
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);
  const requestRef = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const request = requestRef.current + 1;

    requestRef.current = request;
    setLoading(true);

    const result = await EventSetupService.getCatalog();

    if (requestRef.current !== request) {
      return;
    }

    if (result.code === OP_RESULT_CODES.OK) {
      setCatalog(result.getDataFirst() ?? null);
      setLastError(null);
    } else {
      setLastError(result);
    }

    // ⚠ Released on the failure path too — a failed first read must not hold the loading state for ever.
    setLoading(false);
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (autoLoad) {
      load();
    }
  }, [autoLoad, load]);

  const types = useMemo(() => (Array.isArray(catalog?.types) ? catalog!.types : []), [catalog]);
  const rules = useMemo(() => (Array.isArray(catalog?.rules) ? catalog!.rules : []), [catalog]);

  const byKey = useMemo(() => {
    const map = new Map<string, EventSetupType>();

    types.forEach((t) => {
      map.set(t.id, t);
      map.set(t.code, t);
    });

    return map;
  }, [types]);

  const findType = useCallback((idOrCode: string | null | undefined) => (idOrCode ? byKey.get(idOrCode) ?? null : null), [byKey]);

  const parentOf = useCallback((type: EventSetupType | null | undefined) => (type?.parentId ? byKey.get(type.parentId) ?? null : null), [byKey]);

  const topLevelOfGroup = useCallback((group: string) => types.filter((t) => !t.parentId && t.categoryGroup === group), [types]);

  const childrenOf = useCallback((typeId: string | null | undefined) => (typeId ? types.filter((t) => t.parentId === typeId) : []), [types]);

  const allowsRecurring = useCallback((idOrCode: string | null | undefined) => findType(idOrCode)?.allowsRecurring !== false, [findType]);

  const search = useCallback((query: string) => searchEventTypes(types, query), [types]);

  return { types, rules, loading, isLoaded, lastError, reload: load, findType, parentOf, topLevelOfGroup, childrenOf, allowsRecurring, search };
};
